/**
 * @fileoverview Component that displays an indicator and menu for active strategic analysis sessions in the top navigation bar.
 * Provides options to navigate to or stop the current session.
 * @module features/layouts/standardPage/components/strategicSessionIndicator
 */

import * as React from 'react';
import { FunctionComponent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, ButtonMenu } from 'basecamp';
import {
	getAppliedDefaultExamLevels,
	showStrategicSessionModal,
} from 'features/strategicAnalysis/redux';
import levels from '../../../../utils/examLevel';
import { translate } from '../../../../utils/locale';
import { useHistory } from 'react-router-dom';
import { styles } from './common';

/**
 * Props for the StrategicSessionIndicator component
 * @typedef {Object} Props
 * @property {boolean} isSmallScreen - Whether the component is being rendered on a small screen device
 * @property {function} setSmallScreenMenuIsOpen - Callback to set the small screen menu open state
 * @property {function} closeAllOpenTopBarMenus - Callback to close all open top bar menus
 */
type Props = {
	isSmallScreen: boolean;
	setSmallScreenMenuIsOpen: (isOpen: boolean) => void;
	closeAllOpenTopBarMenus: () => void;
};

/**
 * Component that renders a button menu for managing strategic analysis sessions.
 * Only displays when there is an active session and user has permission.
 *
 * Features:
 * - Shows current exam levels being analyzed (e.g. "GCSE & A-Level")
 * - Provides menu options to:
 *   - Navigate to the active session
 *   - Stop the current session
 * - Integrates with the top navigation bar responsively
 * - Handles small screen menu state
 *
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.isSmallScreen - Whether component is rendered on small screen
 * @param {function} props.setSmallScreenMenuIsOpen - Controls small screen menu visibility
 * @param {function} props.closeAllOpenTopBarMenus - Closes all open top menus
 * @returns {JSX.Element|null} ButtonMenu component or null if no active session
 */
const StrategicSessionIndicator: FunctionComponent<Props> = ({
	isSmallScreen,
	setSmallScreenMenuIsOpen,
	closeAllOpenTopBarMenus,
}) => {
	/**
	 * React Router history object for navigation
	 * Used to programmatically navigate to the strategic analysis page
	 */
	const history = useHistory();

	/**
	 * Redux hooks and selectors
	 * dispatch - Used to dispatch Redux actions
	 * getExamLevels - Array of currently selected exam levels for analysis
	 */
	const dispatch = useDispatch();
	const getExamLevels = useSelector(getAppliedDefaultExamLevels).map((level) => level.value);

	/**
	 * Formats the exam levels into a human-readable display string
	 * Joins multiple levels with commas and ampersands
	 * @example ['GCSE', 'A-Level'] becomes 'GCSE & A-Level'
	 * @example ['GCSE', 'AS', 'A-Level'] becomes 'GCSE, AS & A-Level'
	 */
	const examLevels = getExamLevels.map((examLevel) => {
		return levels[examLevel as keyof typeof levels].display;
	});
	const sessionType = [examLevels.slice(0, -1).join(', '), examLevels.slice(-1)[0]].join(
		examLevels.length < 2 ? '' : ' & '
	);

	return (
		<Box {...styles(isSmallScreen)} onClick={() => closeAllOpenTopBarMenus()}>
			<ButtonMenu
				setAs="headerLink"
				menuContent={[
					{
						buttonText: 'Go to Session',
						setAs: 'success',
						onClick: () => {
							history.push('/strategic-analysis');
						},
					},
					{
						buttonText: 'Stop Session',
						setAs: 'error',
						onClick: () => {
							dispatch(showStrategicSessionModal());
							setSmallScreenMenuIsOpen(false);
						},
					},
				]}
				triggerText={translate('strategicAnalysis.standardPage.SESSION', { sessionType })}
			/>
		</Box>
	);
};

export default StrategicSessionIndicator;
